import type { IPoiPendingImage, IPoiTranslation, PoiType } from '@/shared/api/types/poi-types'

export interface PoiBasicInfoDraftState {
  category: string
  type: PoiType | ''
  popupAd: boolean
  range: number
}

export interface PoiDraftLocation {
  latitude: number
  longitude: number
  range: number
  address: string
}

export interface PersistedPoiDraft {
  basicInfo: PoiBasicInfoDraftState
  location: PoiDraftLocation
  translations: IPoiTranslation[]
  images: IPoiPendingImage[]
  selectedTenantDestinationId: string
}

export interface BuildPoiDraftInput {
  basicInfo: PoiBasicInfoDraftState
  location: PoiDraftLocation
  translations: IPoiTranslation[]
  images: IPoiPendingImage[]
  selectedTenantDestinationId: string
}

export interface PoiDraftDefaults {
  basicInfo: PoiBasicInfoDraftState
  location: PoiDraftLocation
}

export interface PoiCategoryDraftTranslation {
  language: string
  name: string
  description: string
}

export interface PersistedPoiCategoryDraft {
  icon: string
  iconActive: string
  color: string
  translations: PoiCategoryDraftTranslation[]
}

export interface BuildPoiCategoryDraftInput {
  icon: string
  iconActive: string
  color: string
  translations: PoiCategoryDraftTranslation[]
}

export function sanitizeDraftImages(images: IPoiPendingImage[]): IPoiPendingImage[] {
  return images
    .filter((image) => image.status !== 'uploading')
    .map((image) => {
      const fallbackUrl =
        image.url ?? (image.previewUrl.startsWith('blob:') ? '' : image.previewUrl)
      return {
        ...(image.id ? { id: image.id } : {}),
        ...(image.url ? { url: image.url } : {}),
        ...(image.blobName ? { blobName: image.blobName } : {}),
        ...(image.error ? { error: image.error } : {}),
        ...(image.status ? { status: image.status } : {}),
        previewUrl: fallbackUrl,
      }
    })
    .filter((image) => image.previewUrl.trim().length > 0)
}

export function buildPoiDraft(input: BuildPoiDraftInput): PersistedPoiDraft {
  return {
    basicInfo: input.basicInfo,
    location: input.location,
    translations: input.translations,
    images: sanitizeDraftImages(input.images),
    selectedTenantDestinationId: input.selectedTenantDestinationId,
  }
}

export function hasPoiDraftContent(input: BuildPoiDraftInput, defaults: PoiDraftDefaults): boolean {
  const basicInfoChanged =
    input.basicInfo.category !== defaults.basicInfo.category ||
    input.basicInfo.type !== defaults.basicInfo.type ||
    input.basicInfo.popupAd !== defaults.basicInfo.popupAd ||
    input.basicInfo.range !== defaults.basicInfo.range
  const locationChanged =
    input.location.latitude !== defaults.location.latitude ||
    input.location.longitude !== defaults.location.longitude ||
    input.location.range !== defaults.location.range ||
    input.location.address.trim() !== defaults.location.address.trim()
  const hasTranslationContent = input.translations.some(
    (row) => row.name.trim() || row.description.trim() || row.audioUrl?.trim(),
  )
  return (
    basicInfoChanged ||
    locationChanged ||
    input.selectedTenantDestinationId.trim().length > 0 ||
    sanitizeDraftImages(input.images).length > 0 ||
    hasTranslationContent
  )
}

export function buildPoiCategoryDraft(
  input: BuildPoiCategoryDraftInput,
): PersistedPoiCategoryDraft {
  return {
    icon: input.icon,
    iconActive: input.iconActive,
    color: input.color,
    translations: input.translations,
  }
}

export function hasPoiCategoryDraftContent(input: BuildPoiCategoryDraftInput): boolean {
  return (
    input.icon.trim().length > 0 ||
    input.iconActive.trim().length > 0 ||
    input.translations.some((row) => row.name.trim())
  )
}
